import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import type { PaginatedResponse } from '@/shared/types/api'

// ── Types ────────────────────────────────────────────────

export interface TranslationLanguage {
  id: number
  code: string
  name: string
  is_active: boolean
  created_at: string
}

export interface TranslationKeyWithValues {
  id: number
  key: string
  description: string
  source: 'FE' | 'BE'
  global_value: string | null
  tenant_value: string | null
  tenant_value_id: number | null
  created_at: string
  updated_at: string
}

export interface TranslationValue {
  id: number
  key: string
  key_id: number
  language_code: string
  tenant_uuid: string | null
  value: string
  created_at: string
  updated_at: string
}

// ── Languages ────────────────────────────────────────────

export function useLanguages() {
  return useQuery<TranslationLanguage[]>({
    queryKey: ['translation-languages'],
    queryFn: () => api.get('/translations/languages/').then((r) => r.data),
  })
}

// ── Keys (with annotated values for staff table) ─────────

export function useTranslationKeys(
  params?: {
    source?: string
    search?: string
    language?: string
    page?: number
    page_size?: number
    ordering?: string
    /** Tenant view: only keys this tenant has overridden ("Your wording"). */
    overridden?: boolean
  },
  options?: { enabled?: boolean },
) {
  return useQuery<PaginatedResponse<TranslationKeyWithValues>>({
    queryKey: ['translation-keys', params],
    queryFn: () =>
      api
        .get<PaginatedResponse<TranslationKeyWithValues>>('/translations/keys/', {
          params: { page_size: 200, ...params },
        })
        .then((r) => r.data),
    ...options,
  })
}

export function useCreateTranslationKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { key: string; description?: string; source: string }) =>
      api.post('/translations/keys/', data).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['translation-keys'] }),
  })
}

export function useUpdateTranslationKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number
      key: string
      description?: string
      source: string
    }) => api.put(`/translations/keys/${id}/`, data).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['translation-keys'] }),
  })
}

export function useDeleteTranslationKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/translations/keys/${id}/`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['translation-keys'] }),
  })
}

// ── Values ───────────────────────────────────────────────

export function useUpsertTranslationValue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      key_id: number
      language_code: string
      tenant_uuid?: string | null
      value: string
    }) => api.post('/translations/values/', data).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['translation-keys'] }),
  })
}

export function useDeleteTranslationValue() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete(`/translations/values/${id}/`),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['translation-keys'] }),
  })
}

// ── Import / Export ──────────────────────────────────────

export function useImportTranslations() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      language_code: string
      source: string
      tenant_uuid?: string | null
      data: Record<string, string>
    }) => api.post('/translations/import/', data).then((r) => r.data),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['translation-keys'] }),
  })
}

export function useExportTranslations(params?: {
  language?: string
  source?: string
  scope?: string
}) {
  return useQuery<Record<string, string>>({
    queryKey: ['translation-export', params],
    queryFn: () =>
      api.get('/translations/export/', { params }).then((r) => r.data),
    enabled: false, // only fetch on demand
  })
}
